<template>
  <div class="defib-check">
    <div class="energy-container">
      <el-input 
        v-integer-only
        type="number"
        :value="state.check.energy.min"
        placeholder="any"
        class="energy-min-input"
        @input="v => updateEnergy('min', parseInt(v))"
      >
        <template slot="prepend">
          <help-tooltip v-if="defibEnabledWarning" type="warning">
            {{ defibEnabledWarning.message }}
          </help-tooltip>
          If shocked with energy between
          <help-tooltip v-if="energyWarning" type="warning">
            {{ energyWarning.message }}
          </help-tooltip>
        </template>
      </el-input>
      <el-input 
        v-integer-only
        type="number"
        :value="state.check.energy.max"
        placeholder="any"
        class="energy-max-input"
        @input="v => updateEnergy('max', parseInt(v))"
      >
        <template slot="prepend">and</template>
        <template slot="append">
          <help-tooltip v-if="syncEnabledWarning" type="warning">
            {{ syncEnabledWarning.message }}
          </help-tooltip>
          <el-switch
            :value="state.check.isSync"
            on-text="sync on"
            off-text="sync off"
            on-color="#62cd63"
            off-color="#f92c2d"
            :width="110"
            class="sync-switch"
            @input="v => updateIsSync(v)"
          />
        </template>
      </el-input>
    </div>
    <div class="energy-container">
      <el-select
        v-close-on-zoom
        :value="state.check.operator"
        placeholder="exactly"
        class="operator-select"
        :disabled="readonly"
        @input="operator => updateOperator(operator)"
      >
        <el-option v-for="op in operators" :key="op.value" :label="op.label" :value="op.value" />
      </el-select>
      <el-input
        v-integer-only
        type="number"
        placeholder="any amount of"
        :disabled="readonly"
        :value="state.check.count"
        :min="0"
        class="count-input"
        @change="value => updateCount(parseInt(value))"
      >
        <template slot="append">times</template>
      </el-input>
      <help-tooltip v-if="countWarning" type="warning">
        {{ countWarning.message }}
      </help-tooltip>
    </div>
  </div>
</template>
 
<script>
import get from 'lodash/get';
import isNumber from 'lodash/isNumber';
import uniqBy from 'lodash/uniqBy';

import HelpTooltip from '../../common/components/HelpTooltip';

export default {
  name: 'defib-check',
  components: {
    HelpTooltip,
  },
  props: ['readonly', 'emitter', 'ikey', 'getData', 'putData', 'control'],
  data() {
    return {
      state: {
        check: {
          energy: {
            min: 0,
            max: 0,
          },
          isSync: false,
          count: null,
          operator: 'EQUAL',
        },
      },
      operators: [
        { value: 'LESS_THAN', label: 'less than' },
        { value: 'EQUAL', label: 'exactly' },
        { value: 'GREATER_THAN', label: 'more than' },
      ],
    };
  },
  computed: {
    defibTreatment() {
      const { caseConf } = this.emitter;
      return caseConf._treatments.find(t => t.id === 'DEFIBRILLATION');
    },
    energyBounds() {
      return get(
        this.defibTreatment,
        'options.componentOptions.ENERGY.allowedRange',
        [0, 0],
      );
    },
    warnings() {
      if (!this.emitter.$store) {
        return [];
      }
      return uniqBy(
        get(
          this.emitter.$store.getters['validation/visibleWarningsByFieldAndId'],
          `caseLogicDescription.${this.control.parent.id}`,
          [],
        ).filter(
          warning => warning.metadata && warning.metadata.scope === 'control' && warning.metadata.checkKey === this.ikey,
        ),
        'message',
      );
    },
    defibEnabledWarning() {
      return this.warnings.find(warning => warning.metadata.componentId === 'DEFIBRILLATION');
    },
    syncEnabledWarning() {
      return this.warnings.find(warning => warning.metadata.componentId === 'SYNC');
    },
    energyWarning() {
      return this.warnings.find(warning => warning.metadata.componentId === 'ENERGY');
    },
    countWarning() {
      return this.warnings.find(warning => warning.metadata.countWarning);
    }
  },
  mounted() {
    this.state.check = { ...this.state.check, ...this.getData(this.ikey) };
    this.state.isSyncEnabled = this.state.check.id === 'SYNC';
    // Populate with defaults when the check is created
    if (!this.getData(this.ikey)) {
      this.state.check.energy = {
        min: this.energyBounds[0],
        max: this.energyBounds[1],
      };
    }
    this.emitter.trigger('process', this.control);
    this.update();
  },
  methods: {
    updateEnergy(key, value) {
      this.state.check.energy[key] = parseFloat(value);
      this.update();
    },
    updateCount(count) {
      if (isNumber(count)) {
        this.state.check.count = count;
      } else {
        this.state.check.count = null;
      }
      this.update();
    },
    updateOperator(operator) {
      this.state.check.operator = operator;
      this.update();
    },
    updateIsSync(isSync) {
      this.state.check.isSync = isSync;
      this.update();
    },
    update() {
      this.putData(this.ikey, {
        ...this.state.check,
        type: 'PACING',
        id: this.state.check.isSync ? 'SYNC' : 'DEFIBRILLATION',
      });
      this.emitter.trigger('process', this.control);
    },
  },
};
</script>
 
<style scoped>
.defib-check {
  width: 100%;
  color: black;
}

.energy-container {
  display: flex;
  align-items: center;
}

.help-tooltip {
  margin-left: 5px;
}

.operator-select {
  width: 110px;
}
.count-input {
  width: 190px;
}

.help-tooltip {
  margin: 0 4px;
}
</style>
